/*
 * Startup capability checks (spec sec. 4).
 *
 * These run before any 3D asset is requested so an unsupported visitor never
 * pays the download cost (spec sec. 9). The friendly full-screen versions of
 * these messages are finished in Phase 6; Phase 1 only needs the gate.
 */

/**
 * Coarse desktop-vs-touch detection. We are intentionally conservative: the
 * prototype requires keyboard and mouse, so anything that looks primarily
 * touch-driven or narrow is treated as unsupported.
 *
 * @returns {boolean} true when the device looks like a phone or tablet.
 */
export function isMobileOrTablet() {
  if (typeof navigator === "undefined") return false;

  const uaData = navigator.userAgentData;
  if (uaData && typeof uaData.mobile === "boolean" && uaData.mobile) {
    return true;
  }

  const ua = navigator.userAgent || "";
  const mobileUA =
    /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  // iPadOS reports a desktop UA but exposes touch with a coarse pointer.
  const iPadOS =
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  const genericTablet = /\b(iPad|Tablet|PlayBook|Silk)\b/i.test(ua);

  const coarsePrimaryPointer =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches &&
    !window.matchMedia("(any-pointer: fine)").matches;

  return mobileUA || iPadOS || genericTablet || coarsePrimaryPointer;
}

/**
 * Check for a usable WebGL 2 context (spec sec. 4). Does not keep the context.
 *
 * @returns {boolean}
 */
export function hasWebGL2() {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2");
    return Boolean(gl);
  } catch {
    return false;
  }
}

/**
 * Run every gate and report the first failure, if any.
 *
 * @returns {{ ok: boolean, screen?: "mobile" | "unsupported", reason?: string }}
 */
export function checkEnvironment() {
  if (isMobileOrTablet()) {
    return { ok: false, screen: "mobile" };
  }
  if (!hasWebGL2()) {
    return {
      ok: false,
      screen: "unsupported",
      reason:
        "This browser does not support WebGL 2, which the prototype requires.",
    };
  }
  return { ok: true };
}
